import { useCallback, useEffect, useRef } from "react"

/**
 * Returns an `animate(fullText, onUpdate, opts?)` that reveals `fullText` into
 * `onUpdate` progressively — a "the AI is writing it" typewriter effect for the
 * fields where generated text lands (email subject + the letter/content editor).
 *
 * The backend returns the whole completion at once (not a token stream), so this
 * animates the already-received text. Any in-flight animation is cancelled when a
 * new one starts or the component unmounts.
 */
export function useTypewriter() {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => cancel, [cancel])

  return useCallback(
    (full: string, onUpdate: (partial: string) => void, opts?: { onDone?: () => void }) => {
      cancel()
      const text = full ?? ""
      if (!text) {
        onUpdate("")
        opts?.onDone?.()
        return
      }
      const total = text.length
      const tick = 24 // ms between frames
      // Scale the duration with length but keep it snappy (≈0.45s–2s).
      const durationMs = Math.min(2000, Math.max(450, total * 4))
      const steps = Math.max(1, Math.round(durationMs / tick))
      const chunk = Math.max(1, Math.ceil(total / steps))
      let i = 0
      timerRef.current = setInterval(() => {
        i = Math.min(total, i + chunk)
        onUpdate(text.slice(0, i))
        if (i >= total) {
          cancel()
          opts?.onDone?.()
        }
      }, tick)
    },
    [cancel],
  )
}
